import { MultimediaType } from 'utils/constants';
import { isEmptyString } from 'utils/helper';

const isSafari = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('safari') && !userAgent.includes('chrome');
};

export const mimeTypesByCategory: Record<string, string[]> = Object.freeze({
  [MultimediaType.ProfilePicture]: ['png', 'jpg', 'jpeg'],
  [MultimediaType.ClaimAttachment]: ['pdf', 'png', 'jpg', 'jpeg', 'tif', 'tiff'],
  all: ['doc', 'pdf', 'rtf', 'docx', 'png', 'jpg', 'jpeg', 'tif', 'tiff', 'heic'],
});

export function getAllowedFileTypesByCategory(mediaCategory?: MultimediaType): string {
  const extensions = mimeTypesByCategory[mediaCategory ?? 'all'] ?? mimeTypesByCategory.all;
  return extensions
    .map((extension: string) => `.${extension}`)
    .join(',');
}

export function getIsExtensionDisplayableInImgTag(extension: string): boolean {
  return /png|jpg|jpeg/i.test(extension);
}

export function getIsExtensionDisplayableInIFrameTag(extension: string): boolean {
  return /pdf/i.test(extension);
}

export function getSupportedFilesMessage(): string {
  // Don't bother mentioning file types we can't preview
  return 'Supported file types: jpeg, jpg, pdf, png, tiff, tif';
}

const downloadFile = (blob: Blob, fileName: string) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = fileName || 'download';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

export const downloadFromUrl = async (url: string, fileName: string) => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
  }

  const blob = await response.blob();

  // Check if browser is Safari
  if (isSafari()) {
    const downloadUrl = window.URL.createObjectURL(blob);
    const newWindow = window.open(downloadUrl, '_blank');

    // Clean up the object URL after a delay to ensure the new tab has loaded
    setTimeout(() => {
      window.URL.revokeObjectURL(downloadUrl);
    }, 1000);

    if (!newWindow) {
      console.warn('Popup blocked - falling back to download method');
      // Fallback to download method if popup is blocked
      downloadFile(blob, fileName);
    }
  } else {
    downloadFile(blob, fileName);
  }
};

export const printPdfFromUrl = async (url: string) => {
  if (!isEmptyString(url)) {
    // eslint-disable-next-line import/no-extraneous-dependencies
    const printJsModule = await import('print-js');
    const printJS = (printJsModule as any).default ?? (printJsModule as any);
    printJS({
      printable: url,
      type: 'pdf',
      showModal: true,
    });
  }
};
